import { useEffect, useState, useMemo } from 'react';
import { account } from '@/lib/api';
import {
  RefreshCw,
  AlertCircle,
  Loader2,
  Users,
  TrendingDown,
  UserX,
  Lightbulb,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
} from 'lucide-react';

// =============================================================================
// Types (mirrors backend LicenseUtilizationReport)
// =============================================================================

interface LicenseReport {
  generatedAt: string;
  account: { name: string; type: string; accountNumber?: string };
  plans: Array<{
    name: string;
    type: string;
    seats: number;
    used: number;
    utilizationPct: number;
  }>;
  userCounts: {
    total: number;
    licensed: number;
    basic: number;
    inactive30: number;
    inactive60: number;
    inactive90: number;
    neverLoggedIn: number;
  };
  inactiveUsers: Array<{
    email: string;
    name: string;
    type: 'licensed' | 'basic';
    department?: string;
    role?: string;
    lastLogin?: string;
    daysSinceLogin: number;
    addOns: string[];
  }>;
  addOnUtilization: Array<{
    name: string;
    licensedUsers: number;
    activeUsers: number;
    unusedCount: number;
    utilizationPct: number;
  }>;
  savings: Array<{
    category: string;
    description: string;
    affectedUsers: number;
    potentialAction: string;
  }>;
}

// =============================================================================
// Main Page
// =============================================================================

export function LicenseReportPage() {
  const [report, setReport] = useState<LicenseReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchReport() {
    setLoading(true);
    setError(null);
    try {
      const result = await account.getLicenseReport({});
      setReport(result.report as unknown as LicenseReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load license report');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReport();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="rounded-full bg-red-500/10 p-4">
          <AlertCircle className="h-10 w-10 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">Failed to Load Report</h2>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <button
          onClick={fetchReport}
          className="flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 space-y-6">
        <Header report={report} onRefresh={fetchReport} />
        <SummaryCards report={report} />
        <PlansTable plans={report.plans} />
        <InactiveUsersTable users={report.inactiveUsers} />
        {report.addOnUtilization.length > 0 && (
          <AddOnUtilization addOns={report.addOnUtilization} />
        )}
        {report.savings.length > 0 && (
          <SavingsRecommendations savings={report.savings} />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Header
// =============================================================================

function Header({ report, onRefresh }: { report: LicenseReport; onRefresh: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-xl font-bold text-foreground">License Utilization</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {report.account.name} &middot; Generated {new Date(report.generatedAt).toLocaleString()}
        </p>
      </div>
      <button
        onClick={onRefresh}
        className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
      >
        <RefreshCw className="h-4 w-4" />
        <span className="hidden sm:inline">Refresh</span>
      </button>
    </div>
  );
}

// =============================================================================
// Summary Cards
// =============================================================================

function SummaryCards({ report }: { report: LicenseReport }) {
  const basePlan = report.plans.find(p => p.type === 'base');
  const utilizationPct = basePlan?.utilizationPct ?? (
    report.userCounts.total > 0
      ? Math.round((report.userCounts.licensed / report.userCounts.total) * 100)
      : 0
  );

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <SummaryCard
        icon={<Users className="h-5 w-5 text-blue-400" />}
        label="Total Users"
        value={report.userCounts.total}
        detail={`${report.userCounts.licensed} licensed, ${report.userCounts.basic} basic`}
      />
      <SummaryCard
        icon={<TrendingDown className="h-5 w-5 text-green-400" />}
        label="License Utilization"
        value={`${utilizationPct}%`}
        detail={basePlan ? `${basePlan.used} of ${basePlan.seats} seats` : undefined}
      />
      <SummaryCard
        icon={<UserX className="h-5 w-5 text-orange-400" />}
        label="Inactive (90+ days)"
        value={report.userCounts.inactive90}
        detail={report.userCounts.neverLoggedIn > 0 ? `${report.userCounts.neverLoggedIn} never logged in` : undefined}
      />
      <SummaryCard
        icon={<Lightbulb className="h-5 w-5 text-yellow-400" />}
        label="Savings Opportunities"
        value={report.savings.length}
        detail={report.savings.length > 0 ? 'See recommendations below' : 'No issues found'}
      />
    </div>
  );
}

function SummaryCard({ icon, label, value, detail }: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  detail?: string;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-foreground">{value}</p>
      {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

// =============================================================================
// Plans Table
// =============================================================================

function PlansTable({ plans }: { plans: LicenseReport['plans'] }) {
  if (plans.length === 0) return null;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span className="text-muted-foreground">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </span>
        <h3 className="flex-1 text-sm font-semibold text-foreground">Plans & Seats</h3>
      </div>
      <div className="p-5 space-y-4">
        {plans.map((plan, i) => (
          <div key={i} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{formatPlanName(plan.name)}</span>
              <span className="text-sm text-muted-foreground">
                {plan.used} / {plan.seats} seats ({plan.utilizationPct}%)
              </span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  plan.utilizationPct >= 90 ? 'bg-red-500'
                    : plan.utilizationPct >= 70 ? 'bg-yellow-500'
                    : 'bg-primary'
                }`}
                style={{ width: `${Math.min(100, plan.utilizationPct)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatPlanName(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// =============================================================================
// Inactive Users Table
// =============================================================================

type SortField = 'daysSinceLogin' | 'name' | 'email' | 'department';
type SortDir = 'asc' | 'desc';

function InactiveUsersTable({ users }: { users: LicenseReport['inactiveUsers'] }) {
  const [sortField, setSortField] = useState<SortField>('daysSinceLogin');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(() => {
    const copy = [...users];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'daysSinceLogin': cmp = a.daysSinceLogin - b.daysSinceLogin; break;
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'email': cmp = a.email.localeCompare(b.email); break;
        case 'department': cmp = (a.department ?? '').localeCompare(b.department ?? ''); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [users, sortField, sortDir]);

  const displayed = showAll ? sorted : sorted.slice(0, 20);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'daysSinceLogin' ? 'desc' : 'asc');
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3 w-3" />
      : <ChevronDown className="h-3 w-3" />;
  }

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <UserX className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Inactive Licensed Users</h3>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-500/10 text-orange-400">
          {users.length} users
        </span>
      </div>
      <div className="p-5">
        {users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No inactive licensed users found. All licenses are being actively used.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="pb-2 pr-4 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                      <span className="inline-flex items-center gap-1">Name <SortIcon field="name" /></span>
                    </th>
                    <th className="pb-2 pr-4 cursor-pointer select-none" onClick={() => toggleSort('email')}>
                      <span className="inline-flex items-center gap-1">Email <SortIcon field="email" /></span>
                    </th>
                    <th className="pb-2 pr-4 cursor-pointer select-none" onClick={() => toggleSort('department')}>
                      <span className="inline-flex items-center gap-1">Department <SortIcon field="department" /></span>
                    </th>
                    <th className="pb-2 pr-4">Role</th>
                    <th className="pb-2 pr-4 cursor-pointer select-none" onClick={() => toggleSort('daysSinceLogin')}>
                      <span className="inline-flex items-center gap-1">Days Inactive <SortIcon field="daysSinceLogin" /></span>
                    </th>
                    <th className="pb-2">Add-Ons</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {displayed.map((user, i) => (
                    <tr key={i} className="hover:bg-secondary/30">
                      <td className="py-2 pr-4 text-foreground">{user.name}</td>
                      <td className="py-2 pr-4 text-muted-foreground text-xs">{user.email}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{user.department || '—'}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{user.role || '—'}</td>
                      <td className="py-2 pr-4">
                        <InactivityBadge days={user.daysSinceLogin} lastLogin={user.lastLogin} />
                      </td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-1">
                          {user.addOns.length > 0 ? user.addOns.map(a => (
                            <span key={a} className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-400">
                              {a}
                            </span>
                          )) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length > 20 && (
              <button onClick={() => setShowAll(!showAll)} className="mt-3 text-sm text-primary hover:underline">
                {showAll ? 'Show less' : `Show all ${users.length} users`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InactivityBadge({ days, lastLogin }: { days: number; lastLogin?: string }) {
  const variant = days >= 9999
    ? 'bg-red-500/10 text-red-400'
    : days >= 90
      ? 'bg-orange-500/10 text-orange-400'
      : days >= 60
        ? 'bg-yellow-500/10 text-yellow-500'
        : 'bg-secondary text-muted-foreground';

  const label = days >= 9999 ? 'Never' : `${days}d`;

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${variant}`} title={lastLogin ? `Last login: ${new Date(lastLogin).toLocaleDateString()}` : 'Never logged in'}>
      {label}
    </span>
  );
}

// =============================================================================
// Add-On Utilization
// =============================================================================

function AddOnUtilization({ addOns }: { addOns: LicenseReport['addOnUtilization'] }) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <TrendingDown className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Add-On Utilization</h3>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {addOns.map((addOn) => (
            <div key={addOn.name} className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{addOn.name}</span>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  addOn.utilizationPct >= 80 ? 'bg-green-500/10 text-green-500'
                    : addOn.utilizationPct >= 50 ? 'bg-yellow-500/10 text-yellow-500'
                    : 'bg-red-500/10 text-red-400'
                }`}>
                  {addOn.utilizationPct}%
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    addOn.utilizationPct >= 80 ? 'bg-green-500'
                      : addOn.utilizationPct >= 50 ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${addOn.utilizationPct}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{addOn.activeUsers} active</span>
                <span>{addOn.unusedCount} unused</span>
                <span>{addOn.licensedUsers} total</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Savings Recommendations
// =============================================================================

function SavingsRecommendations({ savings }: { savings: LicenseReport['savings'] }) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <Lightbulb className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Savings Recommendations</h3>
      </div>
      <div className="p-5 space-y-3">
        {savings.map((item, i) => (
          <div key={i} className="rounded-lg border border-border p-4 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{item.category}</span>
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-500/10 text-orange-400">
                {item.affectedUsers} users
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{item.description}</p>
            <p className="text-xs text-primary">{item.potentialAction}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
